/**
 * Reference Data API Service
 * Fetches and caches industries and industry subs for quick lookup by ID
 * Data is fetched once and cached - no repeated API calls
 */

import http from '../client';
import { ENDPOINTS } from '../config';

// Cache storage
const CACHE_KEY_INDUSTRIES = 'diil_industries';
const CACHE_KEY_INDUSTRY_SUBS = 'diil_industry_subs';
const CACHE_KEY_JOB_LEVELS = 'diil_job_levels';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// In-memory cache for fast lookups
let industriesCache = null;
let industrySubsCache = null;
let jobLevelsCache = null;
let industriesMap = null;
let industrySubsMap = null;
let jobLevelsMap = null;

/**
 * Get cached data from localStorage
 * @param {string} key
 * @returns {Object|null}
 */
const getCachedData = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_EXPIRY;

    if (isExpired) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

/**
 * Save data to localStorage cache
 * @param {string} key
 * @param {any} data
 */
const setCachedData = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to cache data:', error);
  }
};

/**
 * Build lookup map from array
 * @param {Array} items
 * @returns {Map}
 */
const buildLookupMap = (items) => {
  const map = new Map();
  items.forEach(item => {
    map.set(item.id, item);
    if (item.slug) {
      map.set(`slug:${item.slug}`, item);
    }
  });
  return map;
};

/**
 * Fetch all industries (with caching)
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Array>}
 */
export const getIndustries = async (forceRefresh = false) => {
  // Return from memory cache if available
  if (!forceRefresh && industriesCache) {
    return { success: true, data: industriesCache };
  }

  // Try localStorage cache
  if (!forceRefresh) {
    const cached = getCachedData(CACHE_KEY_INDUSTRIES);
    if (cached) {
      industriesCache = cached;
      industriesMap = buildLookupMap(cached);
      return { success: true, data: cached };
    }
  }

  // Fetch from API
  try {
    const response = await http.get(ENDPOINTS.INDUSTRIES.LIST, null, { skipAuth: true });

    if (response.success && response.data) {
      const data = Array.isArray(response.data) ? response.data : [];
      industriesCache = data;
      industriesMap = buildLookupMap(data);
      setCachedData(CACHE_KEY_INDUSTRIES, data);
      return { success: true, data };
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch all industry subs (with caching)
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Array>}
 */
export const getIndustrySubs = async (forceRefresh = false) => {
  // Return from memory cache if available
  if (!forceRefresh && industrySubsCache) {
    return { success: true, data: industrySubsCache };
  }

  // Try localStorage cache
  if (!forceRefresh) {
    const cached = getCachedData(CACHE_KEY_INDUSTRY_SUBS);
    if (cached) {
      industrySubsCache = cached;
      industrySubsMap = buildLookupMap(cached);
      return { success: true, data: cached };
    }
  }

  // Fetch from API
  try {
    const response = await http.get(ENDPOINTS.INDUSTRY_SUBS.LIST, null, { skipAuth: true });

    if (response.success && response.data) {
      const data = Array.isArray(response.data) ? response.data : [];
      industrySubsCache = data;
      industrySubsMap = buildLookupMap(data);
      setCachedData(CACHE_KEY_INDUSTRY_SUBS, data);
      return { success: true, data };
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get industry by ID (from cache)
 * @param {number|string} id
 * @returns {Promise<Object|null>}
 */
export const getIndustryById = async (id) => {
  // Ensure cache is loaded
  if (!industriesMap) {
    await getIndustries();
  }

  const industry = industriesMap?.get(Number(id));
  return industry || null;
};

/**
 * Get industry by slug (from cache)
 * @param {string} slug
 * @returns {Promise<Object|null>}
 */
export const getIndustryBySlug = async (slug) => {
  // Ensure cache is loaded
  if (!industriesMap) {
    await getIndustries();
  }

  return industriesMap?.get(`slug:${slug}`) || null;
};

/**
 * Get industry sub by ID (from cache)
 * @param {number|string} id
 * @returns {Promise<Object|null>}
 */
export const getIndustrySubById = async (id) => {
  // Ensure cache is loaded
  if (!industrySubsMap) {
    await getIndustrySubs();
  }

  const industrySub = industrySubsMap?.get(Number(id));
  return industrySub || null;
};

/**
 * Get industry sub by slug (from cache)
 * @param {string} slug
 * @returns {Promise<Object|null>}
 */
export const getIndustrySubBySlug = async (slug) => {
  // Ensure cache is loaded
  if (!industrySubsMap) {
    await getIndustrySubs();
  }

  return industrySubsMap?.get(`slug:${slug}`) || null;
};

/**
 * Get industry subs by industry ID (from cache)
 *
 * NOTE: filters the *curated* list that getIndustrySubs() caches, which is the
 * home-page category set — not the wizard's Чиглэл. For that, use getJobGroups().
 * @param {number|string} industryId
 * @returns {Promise<Array>}
 */
export const getIndustrySubsByIndustryId = async (industryId) => {
  // Ensure cache is loaded
  if (!industrySubsCache) {
    await getIndustrySubs();
  }

  return industrySubsCache?.filter(sub => sub.industryId === Number(industryId)) || [];
};

/**
 * The five curated Diil sectors. Public, and small enough to cache in memory for
 * the session — they change about as often as the product's branding does.
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
let sectorsCache = null;

export const getSectors = async () => {
  if (sectorsCache) {
    return { success: true, data: sectorsCache };
  }

  try {
    const response = await http.get(ENDPOINTS.DIIL_SECTORS.LIST, null, { skipAuth: true });
    if (response.success && Array.isArray(response.data)) {
      sectorsCache = response.data;
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Mongolia's administrative divisions. Public and static enough to hold for the session.
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
let locationsCache = null;

export const getLocations = async () => {
  if (locationsCache) {
    return { success: true, data: locationsCache };
  }

  try {
    const response = await http.get(ENDPOINTS.LOCATIONS.LIST, null, { skipAuth: true });
    if (response.success && Array.isArray(response.data)) {
      locationsCache = response.data;
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/* -------------------------------------------------------------------------
 * Job taxonomy — Сектор -> Чиглэл -> Албан тушаал
 *
 * Backs step 1 of the employer post-job wizard. Sectors come from getIndustries();
 * the two levels below are the same /industry-subs resource narrowed by query param.
 *
 * Cached in memory only, not localStorage: a wizard run touches a handful of nodes,
 * and the tree is large enough that persisting all of it would be a poor trade for
 * data that is one cheap request away.
 * ---------------------------------------------------------------------- */

const jobGroupsCache = new Map();
const jobTitlesCache = new Map();

/**
 * Чиглэл within a sector, e.g. "🔧 Засвар, механик" under Уул уурхай.
 * @param {number|string} industryId
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getJobGroups = async (industryId) => {
  const key = Number(industryId);
  if (jobGroupsCache.has(key)) {
    return { success: true, data: jobGroupsCache.get(key) };
  }

  try {
    const response = await http.get(
      ENDPOINTS.INDUSTRY_SUBS.BY_INDUSTRY,
      { industryId: key },
      { skipAuth: true }
    );

    if (response.success && Array.isArray(response.data)) {
      jobGroupsCache.set(key, response.data);
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Албан тушаал within a Чиглэл, e.g. "Механик инженер".
 * @param {number|string} groupId
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const getJobTitles = async (groupId) => {
  const key = Number(groupId);
  if (jobTitlesCache.has(key)) {
    return { success: true, data: jobTitlesCache.get(key) };
  }

  try {
    const response = await http.get(
      ENDPOINTS.INDUSTRY_SUBS.BY_PARENT,
      { parentId: key },
      { skipAuth: true }
    );

    if (response.success && Array.isArray(response.data)) {
      jobTitlesCache.set(key, response.data);
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Type-ahead over Албан тушаал, backing the wizard's "✏️ Бусад" free-text entry.
 * Matches anywhere in the name, so "инженер" finds "Механик инженер". Queries
 * shorter than 2 characters return nothing.
 * @param {string} q
 * @param {number|null} industryId scope to one sector — the wizard always passes it,
 *   so a pick can't file the ad under a sector the employer never chose
 * @param {number} limit
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export const searchJobTitles = async (q, industryId = null, limit = 8) => {
  const query = (q || '').trim();
  if (query.length < 2) {
    return { success: true, data: [] };
  }

  try {
    const params = { q: query, limit };
    if (industryId) params.industryId = Number(industryId);

    const response = await http.get(
      ENDPOINTS.INDUSTRY_SUBS.SEARCH,
      params,
      { skipAuth: true }
    );

    if (response.success && Array.isArray(response.data)) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Unexpected response' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch all job levels (with caching)
 * @param {boolean} forceRefresh - Force refresh from API
 * @returns {Promise<Array>}
 */
export const getJobLevels = async (forceRefresh = false) => {
  // Return from memory cache if available
  if (!forceRefresh && jobLevelsCache) {
    return { success: true, data: jobLevelsCache };
  }

  // Try localStorage cache
  if (!forceRefresh) {
    const cached = getCachedData(CACHE_KEY_JOB_LEVELS);
    if (cached) {
      jobLevelsCache = cached;
      jobLevelsMap = buildLookupMap(cached);
      return { success: true, data: cached };
    }
  }

  // Fetch from API
  try {
    const response = await http.get(ENDPOINTS.JOB_LEVELS.LIST, null, { skipAuth: true });

    if (response.success && response.data) {
      const data = Array.isArray(response.data) ? response.data : [];
      jobLevelsCache = data;
      jobLevelsMap = buildLookupMap(data);
      setCachedData(CACHE_KEY_JOB_LEVELS, data);
      return { success: true, data };
    }

    return response;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get job level by ID (from cache)
 * @param {number|string} id
 * @returns {Promise<Object|null>}
 */
export const getJobLevelById = async (id) => {
  // Ensure cache is loaded
  if (!jobLevelsMap) {
    await getJobLevels();
  }

  return jobLevelsMap?.get(Number(id)) || null;
};

/**
 * Initialize all reference data
 * Call this once on app startup to pre-load cache
 * @returns {Promise<void>}
 */
export const initReferenceData = async () => {
  await Promise.all([
    getIndustries(),
    getIndustrySubs(),
    getJobLevels()
  ]);
};

/**
 * Clear reference data cache
 */
export const clearReferenceCache = () => {
  industriesCache = null;
  industrySubsCache = null;
  jobLevelsCache = null;
  industriesMap = null;
  industrySubsMap = null;
  jobLevelsMap = null;
  jobGroupsCache.clear();
  jobTitlesCache.clear();
  localStorage.removeItem(CACHE_KEY_INDUSTRIES);
  localStorage.removeItem(CACHE_KEY_INDUSTRY_SUBS);
  localStorage.removeItem(CACHE_KEY_JOB_LEVELS);
};

/**
 * Force refresh all reference data
 * @returns {Promise<void>}
 */
export const refreshReferenceData = async () => {
  clearReferenceCache();
  await initReferenceData();
};

export default {
  getIndustries,
  getIndustrySubs,
  getJobLevels,
  getIndustryById,
  getIndustryBySlug,
  getIndustrySubById,
  getIndustrySubBySlug,
  getIndustrySubsByIndustryId,
  getJobGroups,
  getJobTitles,
  searchJobTitles,
  getJobLevelById,
  initReferenceData,
  clearReferenceCache,
  refreshReferenceData
};
